import React, { useMemo, useState } from "react";
import { Button, Modal, Skeleton } from "antd";
import { PlusOutlined } from "@ant-design/icons";

import Comments from "components/comments";
import Events from "components/events";
import Icons from "components/icons";
import Stands from "components/stands";
import { Localize } from "components/service";
import { useBreadCrumbs } from "components/use-hooks";
import "components/comments/comments.scss";

const eventBasePath = Events.Const.basePath;
const standBasePath = Stands.Const.basePath;

const StandCommentsSubPage = ({ event, stand }) => {
    const breadcrumbs = [
        {
            label: Localize({ children: "BREADCRUMBS.Label_Exhibitions" }).props
                .children,
            path: `${eventBasePath}`,
        },
        {
            label: event?.title,
            path: `${eventBasePath}/${event?.id}`,
        },
        {
            label: Localize({ children: "BREADCRUMBS.Label_Stands" }).props.children,
            path: `${eventBasePath}/${event?.id}${standBasePath}`,
        },
        {
            label: stand?.exhibitor,
            path: `${eventBasePath}/${event?.id}${standBasePath}/${stand?.id}`,
        },
        {
            label: Localize({ children: "BREADCRUMBS.Label_Comments" }).props
                .children,
            path: `${eventBasePath}/${event?.id}${standBasePath}/${stand?.id}/comments`,
        },
    ];

    useBreadCrumbs(breadcrumbs);

    const standID = +stand?.id;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState("add");
    const [activeComment, setActiveComment] = useState({
        id: null,
        text: "",
        attachments: [],
    });
    const [modalError, setModalError] = useState("");
    const draftSession = Comments.Hooks.draftSession();

    const {
        dataStandComments,
        loadingQueryStandComments,
        refetchStandComments,
    } = Stands.Hooks.queryComments({
        standID,
        first: 100,
    });

    const comments = useMemo(
        () => (dataStandComments?.data || []).map(Comments.Helpers.mapComment),
        [dataStandComments?.data],
    );

    const { _loadStandComment } = Stands.Hooks.lazyQueryComment();

    const {
        _setStandCommentCreate,
    } = Stands.Hooks.commentCreate({
        showSuccessNotification: false,
        showErrorNotification: false,
        onError: (error) => {
            setModalError(Comments.Helpers.parseErrorMessage(error));
        },
    });

    const {
        _setStandCommentUpdate,
        loadingMutationStandCommentUpdate,
    } = Stands.Hooks.commentUpdate({
        showErrorNotification: false,
        onCompleted: () => {
            setIsModalOpen(false);
            draftSession.syncModalOpenState(false);
            setModalError("");
            setActiveComment({
                id: null,
                text: "",
                attachments: [],
            });
            refetchStandComments();
        },
        onError: (error) => {
            setModalError(Comments.Helpers.parseErrorMessage(error));
        },
    });

    const {
        _setStandCommentDelete,
    } = Stands.Hooks.commentDelete({
        showSuccessNotification: true,
        showErrorNotification: true,
        onCompleted: () => {
            refetchStandComments();
        },
        onError: (error) => {
            setModalError(Comments.Helpers.parseErrorMessage(error));
        },
    });

    const {
        _setStandCommentDelete: _setStandCommentDeleteSilent,
    } = Stands.Hooks.commentDelete({
        showSuccessNotification: false,
        showErrorNotification: false,
        onCompleted: () => {
            refetchStandComments();
        },
        onError: (error) => {
            setModalError(Comments.Helpers.parseErrorMessage(error));
        },
    });

    const {
        _setStandAttachmentCreate,
        loadingMutationStandAttachmentCreate,
    } = Stands.Hooks.attachmentCreate({
        showSuccessNotification: false,
        showErrorNotification: false,
        onCompleted: (payload) => {
            const nextAttachment = payload?.attachment;
            if (!nextAttachment?.id) {
                return;
            }
            setActiveComment((prev) => ({
                ...prev,
                attachments: [...prev.attachments, Comments.Helpers.mapAttachment(nextAttachment)],
            }));
        },
        onError: (error) => {
            setModalError(Comments.Helpers.parseErrorMessage(error));
        },
    });

    const {
        _setStandAttachmentDelete,
        loadingMutationStandAttachmentDelete,
    } = Stands.Hooks.attachmentDelete({
        showSuccessNotification: false,
        showErrorNotification: false,
        onError: (error) => {
            setModalError(Comments.Helpers.parseErrorMessage(error));
        },
    });

    const {
        _setStandCommentsExportExcel,
        loadingMutationStandCommentsExportExcel,
    } = Stands.Hooks.commentsExportExcel();

    const syncCommentForModal = async (commentID) => {
        if (!commentID) {
            return;
        }

        const { data } = await _loadStandComment({
            variables: {
                id: +commentID,
            }
        });
        const mapped = Comments.Helpers.mapComment(data?.comment);
        setActiveComment({
            id: mapped.id,
            text: mapped.text,
            attachments: mapped.attachments,
        });
    };

    const openAddModal = async () => {
        const sessionId = draftSession.startAddSession();
        setModalError("");
        setModalMode("add");
        draftSession.syncModalModeState("add");
        setIsModalOpen(true);
        draftSession.syncModalOpenState(true);
        setActiveComment({
            id: null,
            text: "",
            attachments: [],
        });

        const { data } = await _setStandCommentCreate({
            variables: {
                input: {
                    model: "Stand",
                    model_id: standID,
                    content: "",
                }
            }
        }).catch(() => ({ data: null }));

        const draftID = data?.commentCreate?.comment?.id;
        if (!draftID) {
            return;
        }
        const numericDraftId = draftSession.registerDraft(sessionId, draftID);

        if (draftSession.shouldCleanupDraftAfterCreate(sessionId)) {
            _setStandCommentDeleteSilent({
                variables: {
                    id: numericDraftId,
                }
            }).catch(() => { });
            draftSession.clearSession(sessionId);
            return;
        }
    };

    const openEditModal = async (comment) => {
        setModalError("");
        setModalMode("edit");
        draftSession.syncModalModeState("edit");
        setActiveComment({
            id: +comment?.id || null,
            text: comment?.text || "",
            attachments: comment?.attachments || [],
        });
        setIsModalOpen(true);
        draftSession.syncModalOpenState(true);
        await syncCommentForModal(comment?.id).catch((error) => {
            setModalError(Comments.Helpers.parseErrorMessage(error));
        });
    };

    const handleAddAttachment = (file) => {
        const draftCommentID = draftSession.getDraftCommentID(activeComment?.id);
        if (!draftCommentID || activeComment?.attachments?.length >= 3) {
            return;
        }
        setModalError("");
        _setStandAttachmentCreate({
            variables: {
                file,
                ee_id: +event?.id,
                model: "Comment",
                model_id: draftCommentID,
            }
        });
    };

    const handleRemoveAttachment = async (attachmentID) => {
        if (!attachmentID) {
            return;
        }
        await _setStandAttachmentDelete({
            variables: {
                id: +attachmentID,
            }
        });
        setActiveComment((prev) => ({
            ...prev,
            attachments: prev.attachments.filter((item) => item.id !== +attachmentID),
        }));
    };

    const closeModalState = () => {
        setIsModalOpen(false);
        draftSession.syncModalOpenState(false);
        setModalError("");
        setActiveComment({
            id: null,
            text: "",
            attachments: [],
        });
    };

    const handleCancelModal = () => {
        const isAddMode = modalMode === "add";
        const sessionId = draftSession.getCurrentAddSessionId();
        const draftCommentID = draftSession.getDraftCommentID(activeComment?.id);
        const attachmentIds = (activeComment?.attachments || []).map((item) => item.id).filter(Boolean);

        closeModalState();

        if (!isAddMode || !draftCommentID) {
            if (isAddMode) {
                draftSession.requestCleanupForCurrentSession();
            }
            return;
        }

        if (attachmentIds.length) {
            Promise.allSettled(
                attachmentIds.map((attachmentID) =>
                    _setStandAttachmentDelete({
                        variables: {
                            id: +attachmentID,
                        }
                    }),
                ),
            );
        }

        _setStandCommentDeleteSilent({
            variables: {
                id: +draftCommentID,
            }
        }).catch(() => { }).finally(() => {
            draftSession.clearSession(sessionId);
        });
    };

    const handleSubmitModal = ({ text }) => {
        const draftCommentID = draftSession.getDraftCommentID(activeComment?.id);
        if (!draftCommentID) {
            return;
        }
        setModalError("");
        _setStandCommentUpdate({
            variables: {
                input: {
                    id: +draftCommentID,
                    model: "Stand",
                    model_id: standID,
                    content: text || "",
                }
            }
        });
        draftSession.clearCurrentSession();
    };

    const handleDeleteComment = (commentID) => {
        _setStandCommentDelete({
            variables: {
                id: +commentID,
            }
        });
    };

    return (
        <div className="comments-sub-page">

            {!!comments.length && (
                <div className="table-action-bar comments-action-bar">
                    <div className="col">
                        <Button
                            className='ant-btn-secondary'
                            loading={ loadingMutationStandCommentsExportExcel }
                            onClick={ () => {
                                _setStandCommentsExportExcel({
                                    variables: {
                                        stand_id: standID,
                                    }
                                });
                            } }
                        >
                            <Icons.Download />{" "}
                            <Localize>COMMENTS.Button_Text_Export</Localize>
                        </Button>

                        <Button type="primary" onClick={openAddModal}>
                            <PlusOutlined />{" "}
                            <Localize>COMMENTS.Button_Text_AddUpper</Localize>
                        </Button>
                    </div>
                </div>
            )}

            <div className="row-grid">
                <div className="col-left">
                    <div className="comments-content">
                        {loadingQueryStandComments && !comments.length && (
                            <div className="comments-loading-state">
                                {[1, 2, 3].map((item) => (
                                    <div className="comment-card comments-loading-card" key={item}>
                                        <Skeleton
                                            active
                                            title={{ width: "28%" }}
                                            paragraph={{ rows: 3, width: ["100%", "95%", "65%"] }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {!loadingQueryStandComments && !comments.length && (
                            <div className="comments-empty-state">
                                <strong>
                                    <Localize>COMMENTS.Text_NoComments</Localize>
                                </strong>
                                <Icons.CommentsIllustration className="comments-empty-state-icon" />
                                <Button type="primary" onClick={openAddModal}>
                                    <PlusOutlined />{" "}
                                    <Localize>COMMENTS.Button_Text_AddUpper</Localize>
                                </Button>
                            </div>
                        )}

                        {!!comments.length && (
                            <div className="comments-list">
                                {comments.map((comment) => (
                                    <div className="comment-card-wrap" key={comment.id}>
                                        <div className="comment-card-date">
                                            {Comments.Helpers.formatDate(comment.createdAt)}
                                        </div>

                                        <div className="comment-card">
                                            <div className="comment-card-header">
                                                <strong className="comment-card-author">
                                                    {comment.author}
                                                </strong>
                                                <Comments.Fields.Action
                                                    comment={comment}
                                                    onEdit={openEditModal}
                                                    onDelete={handleDeleteComment}
                                                />
                                            </div>

                                            <p className="comment-card-text">{comment.text}</p>

                                            {!!comment.attachments?.length && (
                                                <div className="comment-card-attachments">
                                                    {comment.attachments.map((attachment, index) => {
                                                        const { fileName, extension } = Comments.Helpers.getAttachmentNameParts(attachment);
                                                        const fileBody = (
                                                            <>
                                                                <span className="attachment-icon">
                                                                    <Icons.Attachment />
                                                                </span>
                                                                <span className="comment-attachment-title">
                                                                    <span className="file-name">{fileName}</span>
                                                                    {!!extension && <span>.{extension}</span>}
                                                                </span>
                                                            </>
                                                        );

                                                        return (
                                                            attachment.path ? (
                                                                <a
                                                                    className="comment-attachment-pill"
                                                                    key={`${comment.id}-${index}`}
                                                                    title={attachment.name}
                                                                    href={attachment.path}
                                                                    target="_blank"
                                                                    rel="noreferrer"
                                                                >
                                                                    {fileBody}
                                                                </a>
                                                            ) : (
                                                                <span
                                                                    className="comment-attachment-pill"
                                                                    key={`${comment.id}-${index}`}
                                                                    title={attachment.name}
                                                                >
                                                                    {fileBody}
                                                                </span>
                                                            )
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Modal
                className="modal-standard modal-form comments-modal"
                centered
                visible={isModalOpen}
                width={380}
                footer={null}
                onCancel={handleCancelModal}
                destroyOnClose={false}
            >
                <strong className="modal-title">
                    {modalMode === "edit" ?
                        <Localize>FORMS.Title_EditComment</Localize> :
                        <Localize>FORMS.Title_AddComment</Localize>
                    }
                </strong>
                <Comments.Forms.Edit
                    comment={activeComment}
                    onSubmit={handleSubmitModal}
                    onCancel={handleCancelModal}
                    onAddAttachment={handleAddAttachment}
                    onRemoveAttachment={handleRemoveAttachment}
                    loading={loadingMutationStandCommentUpdate}
                    loadingAttachmentCreate={loadingMutationStandAttachmentCreate}
                    loadingAttachmentDelete={loadingMutationStandAttachmentDelete}
                    errorText={modalError}
                />
            </Modal>
        </div>
    );
};

export default StandCommentsSubPage;
